<template>
    <admin-layout 
    title="View Notification"
    show-back
    backUrl="/contents/email-notification">
        <div>
            <TabComponent
                :tabs="tabs"
            >
            </TabComponent>
        </div>
        <div class="p-7" v-if="currentRoutePath !== `/contents/email-notification/edit/${route.params.id}/logs`">
            <FormSection>
                <template #title>
                    Notification Information
                </template>

                <template #description>
                    Relevant data and other details
                </template>

                <template #form>
                    <div class="col-span-12">
                        <TextInput 
                            label="Subject"
                            placeholder="Subject"
                            v-model="payload.subject"
                            id="subject"
                            name="subject"
                        />
                    </div>
                    <div class="col-span-12">
                        <TextInput 
                            label="Greetings"
                            placeholder="Greetings"
                            v-model="payload.greetings"
                            id="greetings"
                            name="greetings"
                        />
                    </div>
                    <div class="col-span-12 text-gray-600" v-if="item?.greatings_smart_tags">
                        <label class="block text-sm">
                            Required smart tags for greetings:
                        </label>

                        <p>
                            {{ item?.greatings_smart_tags.join(', ') }}
                        </p>
                    </div>
                    <div class="col-span-12">
                        <CKEditor 
                            label="Body"
                            v-model="payload.body"
                            id="body"
                        />
                    </div>
                    <div class="col-span-12 text-red-600" v-if="item?.body_smart_tags">
                        <label class="block text-sm">
                            Required smart tags for body:
                        </label>

                        <p>
                            {{ item?.body_smart_tags.join(', ') }}
                        </p>
                    </div>
                </template>
                <template #actions>
                    <ButtonComponent v-if="$hasPermission('update-content')" @click="showConfirmation = true">
                        Save Changes
                    </ButtonComponent>
                </template>
            </FormSection>
                    
        </div>

        <div v-else>
            <ActivityLogsTable
                :logs="logs"
                @fetch-index="editEmailNotification"
            />
        </div>

        <PageLoader v-if="loader" />

        <ErrorModal
            width="w-[548px]"
            title="Error"
            action-text="Close"
            cancel-text="Go back"
            :show="showError"
            @close="showError = false"
            @confirm="showError = false"
            :errors="errors"
        >
            <template #content>
                <div v-html="errorMsg"/>
            </template>
        </ErrorModal>

        <SuccessModal
            :show="showSuccess"
            @cancel="showSuccess = false"
            @confirm="showSuccess = false"
            action-text="Confirm"
            title="Email Notification Updated!"
            content="Email Notification has been updated successfully"
        />

        <ConfirmationModal
            :show="showConfirmation"
            @cancel="showConfirmation = false"
            @confirm="showConfirmation = false; updateEmailNotification() "
            cancel-text="Cancel"
            action-text="Confirm"
            title="Update Email Notification?"
            content="Are you sure you wish to update this Email Notification?"
        />
       
    </admin-layout>
</template>

<script setup lang="ts">
import AdminLayout from '@/layouts/AdminLayout.vue';
import TabComponent from '@/components/TabComponent.vue';
import FormSection from '@/components/FormSection.vue';
import TextInput from '@/components/inputs/TextInput.vue';
import ButtonComponent from '@/components/ButtonComponent.vue';
import PageLoader from "@/components/PageLoader.vue";
import ErrorModal from '@/components/ErrorModal.vue';
import SuccessModal from '@/components/modal/SuccessModal.vue';
import ConfirmationModal from '@/components/modal/ConfirmationModal.vue';
import ActivityLogsTable from '@/components/ActivityLogsTable.vue';
import SmartTags from '@/components/SmartTags.vue';
import CKEditor from '@/components/inputs/CKEditor.vue';

import EmailNotification from "@/classes/EmailNotification";

import { useRoute, useRouter } from 'vue-router';
import { onMounted, ref, computed } from 'vue';

const route = useRoute();
const router = useRouter();

// Data
const loader    = ref(false);
const showError = ref(false);
const errorMsg  = ref(null);
const errors      = ref([])
const showSuccess = ref(false)
const showConfirmation = ref(false)

const item = ref(null);

const smartTagsText = '<p>You may use the following smart tags to further customize the notification:</p><br/><ul><li>{{email_address}}</li><li>{{first_name}}</li><li>{{last_name}}</li></ul>';
const payload = computed(() => {
    return {
        id: route.params.id,
        subject: null,
        greetings: null,
        body: null
    }
})

const currentRoutePath = computed(() => router.currentRoute.value.path);

const log_counts      = ref(0);
const logs            = ref({});

// Methods
const editEmailNotification = () => {
    loader.value = true;

    EmailNotification.editEmailNotification(route.params.id)
        .then((response: any) => {
            loader.value = false;

            item.value = response.data.emailNotification;
            payload.value.subject = item.value.subject;
            payload.value.greetings = item.value.greetings;
            payload.value.body = item.value.body;

            log_counts.value = response?.data.log_counts
            logs.value = response?.data.logs

        })
        .catch((err) => {
            loader.value = false;
            showError.value = true;
            errorMsg.value = err.response.data.message;
            
            if(err.response.status == 401) {
                window.location.replace('/login');
            }
        })
}

const updateEmailNotification = () => {
    loader.value = true;

    EmailNotification.updateEmailNotification(route.params.id, payload.value)
        .then((response: any) => {
            loader.value = false;
            showSuccess.value = true;
            item.value = response.data.emailNotification;
        })
        .catch((err) => {
            loader.value = false;
            showError.value = true;
            errorMsg.value = err.response.data.message;

            if(err.response.status == 422) {
                errors.value = err.response.data.errors;
            }
            
            if(err.response.status == 401) {
                window.location.replace('/login');
            }
        })
}

const tabs: { name: string, count: number, href?: string, allowed: boolean }[] = computed(() => [
    {
        name: 'Information',
        count: 0,
        href: `/contents/email-notification/edit/${route.params.id}`,
        allowed: true
    },
    {
        name: 'Activity logs',
        count: log_counts.value,
        href: `/contents/email-notification/edit/${route.params.id}/logs`,
        allowed: true
    },
]);

onMounted( () => {
    editEmailNotification();
});

</script>